import {isLeft} from 'fp-ts/lib/Either'
import * as t from 'io-ts'
import reporter from 'io-ts-reporters'
import {Validator as SchemaValidator} from 'jsonschema'
import React, {ReactNode, useCallback, useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import {countChars} from '@atomic.io/common/types/char-limits'
import * as LegacyStyles from './legacy-styles'
import * as Styles from './styles'

const LabelError = styled.span`
  color: ${props => props.theme.red};
  margin-left: 5px;
`

const formatJSON = (value: unknown): string => {
  try {
    return JSON.stringify(
      typeof value === 'object' ? value : JSON.parse(String(value)),
      null,
      2
    )
  } catch {
    return value as string
  }
}

const schemaValidator = new SchemaValidator()

interface BaseJSONFieldProps {
  label?: ReactNode
  style?: React.CSSProperties
  wrapperStyle?: React.CSSProperties
  setValid?: (valid: boolean) => void
  tabIndex?: number
  readOnly?: boolean
  legacyMode?: boolean
  allowEmpty?: boolean
  // Max characters of the compact serialised JSON (ignores formatting
  // whitespace). Marks the field invalid + shows a message when exceeded.
  maxLength?: number
}

interface StringJSONFieldProps extends BaseJSONFieldProps {
  value: unknown
  onChange: (value: string) => void
  raw?: false
}
interface RawJSONFieldProps<A> extends BaseJSONFieldProps {
  value: unknown
  onChange: (value: A) => void
  raw: true
  codec?: t.Type<A>
  schema?: Record<string, unknown>
}
type JSONFieldProps<A = unknown> = StringJSONFieldProps | RawJSONFieldProps<A>

export const JSONField = <A extends {}>(props: JSONFieldProps<A>) => {
  const textAreaRef = useRef(null)
  const [value, setValue] = useState<string>(() => formatJSON(props.value))
  const [validLocal, setValidLocal] = useState<boolean>(true)
  const [errorMessages, setErrorMessages] = useState<string[]>([])

  const {
    setValid: setValidProp,
    value: valueProp,
    raw: rawProp,
    onChange: onChangeProp,
    wrapperStyle,
    maxLength
  } = props

  const codecProp = 'codec' in props ? props.codec : undefined
  const schemaProp = 'schema' in props ? props.schema : undefined

  const setValid = useCallback(
    (valid: boolean) => {
      setValidLocal(valid)
      if (setValidProp) {
        setValidProp(valid)
      }
    },
    [setValidProp]
  )

  // update when value prop changes
  useEffect(() => {
    // Don't format json if the textarea is currently focused / in use
    if (document.activeElement !== textAreaRef.current) {
      // We use a ref above as having an isFocused in state causes us to call this on focus change
      // as well as valueProp change, in that case valueProp can be out of sync with local value
      setValue(formatJSON(valueProp))
    }
  }, [valueProp])

  const validate = useCallback(
    (newValue: string) => {
      setValue(newValue)

      if (props.allowEmpty && newValue === '') {
        setValid(true)
        return undefined
      }
      let parsed
      try {
        parsed = JSON.parse(newValue)
      } catch (e) {
        setValid(false)
        setErrorMessages(
          newValue === '' ? ['Must not be empty'] : ['Invalid JSON']
        )
        return
      }

      if (
        maxLength !== undefined &&
        countChars(JSON.stringify(parsed)) > maxLength
      ) {
        setValid(false)
        setErrorMessages([`Exceeds the ${maxLength} character limit`])
        return
      }

      if (rawProp && codecProp) {
        const result = codecProp.decode(parsed)

        if (isLeft(result)) {
          setValid(false)
          setErrorMessages(reporter.report(result))
          return
        }
        setValid(true)
        return result.right
      }

      if (rawProp && schemaProp) {
        try {
          // Remove $id if present in the schema in a similar way to event-processor
          // as $id is not supported by our schema validator.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const {$id, ...filteredSchemaProp} = schemaProp

          // note the validation of unknown fields is managed by the schema
          // using the additionalProperties property on each object in the schema.
          const validation = schemaValidator.validate(
            parsed,
            filteredSchemaProp,
            {
              allowUnknownAttributes: false,
              required: true
            }
          )

          if (!validation.valid) {
            setValid(false)
            console.warn('JSON is invalid against schema', validation.errors)
            setErrorMessages(['JSON is invalid against schema'])
            return
          }
          setValid(true)
          return parsed
        } catch (e) {
          // If JSON schema itself is invalid for some reason, we don't want to throw an error
          // and break the app. We just want to set the error message.
          setValid(false)
          console.warn('Failed to validate schema', e)
          setErrorMessages(['Invalid schema'])
          return
        }
      }

      setValid(true)
      return rawProp ? parsed : newValue
    },
    [rawProp, codecProp, schemaProp, setValid, props.allowEmpty, maxLength]
  )

  // initial validation on mount and update
  useEffect(() => {
    validate(value)
  }, [value, validate])

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const validated = validate(e.target.value)
      if (validated || props.allowEmpty) {
        onChangeProp(validated)
      }
    },
    [onChangeProp, validate, props.allowEmpty]
  )

  const Wrapper = props.legacyMode ? LegacyStyles.Wrapper : Styles.Wrapper
  const Textarea = props.legacyMode ? LegacyStyles.Textarea : Styles.Textarea
  const Label = props.legacyMode ? LegacyStyles.Label : Styles.Label

  return (
    <Wrapper
      hasLabel={props.label ? true : false}
      readOnly={props.readOnly}
      style={wrapperStyle}
    >
      {props.label ? (
        <Label>
          {props.label}
          {!validLocal ? (
            <LabelError>{errorMessages.join('\n')}</LabelError>
          ) : null}
        </Label>
      ) : null}
      <Textarea
        data-testid="text-area"
        disabled={props.readOnly}
        tabIndex={props.tabIndex}
        ref={textAreaRef}
        style={props.style}
        onBlur={e => {
          setValue(formatJSON(e.target.value))
        }}
        onChange={onChange}
        value={value}
        valid={validLocal}
        readOnly={props.readOnly}
      />
    </Wrapper>
  )
}
